import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, BookOpen } from 'lucide-react';
import { TOKENS, displayFont } from '../../lib/designTokens';
import { articlesApi } from '../../lib/api';

// CRO Audit §5.3 step 5 — Featured guide. Authority proof, evidence-based.
// Pulls the admin-pinned "featured" article (already wired into /api/articles/featured?pinned_only=true).
export const FeaturedGuideBlock = () => {
  const [article, setArticle] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await articlesApi.getFeaturedList(1, true);
        const list = res.data || [];
        if (list[0]) setArticle(list[0]);
        else {
          // Fallback to popular if no pinned
          const pop = await articlesApi.getPopular(1);
          if (pop.data?.[0]) setArticle(pop.data[0]);
        }
      } catch (err) {
        console.error('Featured guide fetch failed:', err);
      }
    })();
  }, []);

  if (!article) return null;

  return (
    <section
      className="py-16 md:py-24"
      style={{ backgroundColor: TOKENS.canvas }}
      data-testid="featured-guide-section"
    >
      <div className="container-ninja">
        <div className="mb-10">
          <p
            className="text-[11px] uppercase tracking-[0.25em] mb-3"
            style={{ color: TOKENS.lime, ...displayFont }}
          >
            Editor&apos;s pick
          </p>
          <h2
            className="text-white text-3xl md:text-4xl lg:text-5xl leading-tight"
            style={displayFont}
          >
            This week&apos;s most-cited guide
          </h2>
        </div>

        <Link
          to={`/blogs/article/${article.slug}`}
          className="group block border transition-all duration-300 hover:-translate-y-1"
          style={{
            backgroundColor: TOKENS.surface1,
            borderColor: 'rgba(255,255,255,0.06)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = TOKENS.limeBorder; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}
          data-testid="featured-guide-card"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
            {/* Image */}
            <div className="md:col-span-5 relative overflow-hidden md:max-h-[420px]">
              {article.featured_image ? (
                <img
                  src={article.featured_image}
                  alt={article.title}
                  className="w-full h-full object-cover aspect-[16/10] md:aspect-auto"
                />
              ) : (
                <div
                  className="w-full h-full aspect-[16/10] md:aspect-auto flex items-center justify-center"
                  style={{ backgroundColor: TOKENS.surface2 }}
                >
                  <BookOpen className="w-12 h-12 text-zinc-700" />
                </div>
              )}
            </div>

            {/* Content */}
            <div className="md:col-span-7 p-7 md:p-10 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4">
                <span
                  className="text-[10px] uppercase tracking-[0.2em] px-2 py-1"
                  style={{
                    color: TOKENS.lime,
                    border: `1px solid ${TOKENS.limeBorder}`,
                    ...displayFont,
                  }}
                >
                  {article.category || 'Guide'}
                </span>
                {article.read_time && (
                  <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
                    <Clock className="w-3 h-3" /> {article.read_time} min read
                  </span>
                )}
              </div>

              <h3
                className="text-white text-2xl md:text-3xl lg:text-4xl mb-4 leading-tight group-hover:text-[#C7FA3E] transition-colors"
                style={displayFont}
              >
                {article.title}
              </h3>

              {article.excerpt && (
                <p className="text-zinc-400 text-sm md:text-base leading-relaxed mb-6 line-clamp-3">
                  {article.excerpt}
                </p>
              )}

              <div
                className="inline-flex items-center gap-2 text-xs uppercase tracking-wider group-hover:gap-3 transition-all"
                style={{ color: TOKENS.lime, ...displayFont }}
              >
                Read the guide <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
};
